import { faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import { PERMISSIONS } from '@/constants/auth.constants';

import useStyles from './TicketTags.styles';
import type { TicketTagsProps } from './TicketTags.types';

export const TicketTags = ({
  tags = [],
  availableTags = [],
  onChange
}: TicketTagsProps) => {
  const { classes } = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  // Local edit/optimistic value, tied to the tags it was derived from.
  const [draft, setDraft] = useState<{
    base: string[];
    value: string[];
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Parent tags changed (not merely a new equal array): drop stale draft.
  if (draft && !isEqual(draft.base, tags)) {
    setDraft(null);
  }

  const selectedTags = draft?.value ?? tags;
  const setSelectedTags = (value: string[]) => setDraft({ base: tags, value });

  const handleTagsChange = useCallback(
    (newTags: string[]) => {
      setSelectedTags(newTags);
      onChange?.(newTags);
    },
    [onChange, tags]
  );

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleSave = useCallback(() => {
    // Only save if the selected tags are different from the original tags
    if (!isEqual(selectedTags, tags)) {
      handleTagsChange(selectedTags);
    }
    setIsEditing(false);
  }, [selectedTags, tags, handleTagsChange]);

  const handleCancel = useCallback(() => {
    setDraft(null);
    setIsEditing(false);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (
        isEditing &&
        !containerRef?.current?.contains(event.target as Node) &&
        !target.closest('.MuiAutocomplete-popper')
      ) {
        handleSave();
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, handleSave]);

  if (isEditing) {
    return (
      <Box ref={containerRef} className={classes.autocompleteContainer}>
        <Autocomplete
          multiple
          disableClearable
          autoFocus
          value={selectedTags}
          options={availableTags}
          onChange={(_, newValue) => setSelectedTags(newValue)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Select tags"
            />
          )}
        />
      </Box>
    );
  }

  return (
    <CheckPermissions permissions={[PERMISSIONS.TICKETS_UPDATE]}>
      <Box className={classes.tagsDisplayContainer} onClick={handleEdit}>
        <Box className={classes.tagsContainer}>
          {selectedTags.length > 0 ? (
            selectedTags.map((tag) => (
              <Chip key={tag} label={tag} variant="filled" />
            ))
          ) : (
            <Typography sx={{ fontStyle: 'italic' }} color="text.secondary">
              No tags
            </Typography>
          )}
        </Box>
        <IconButton
          className={`${classes.editIcon} edit-icon`}
          size="small"
          onClick={handleEdit}
        >
          <FontAwesomeIcon size="1x" icon={faPen} />
        </IconButton>
      </Box>
    </CheckPermissions>
  );
};

TicketTags.displayName = 'TicketTags';

export default TicketTags;
